/* eslint-disable no-restricted-globals */
import React, { createContext, useCallback, useEffect, useReducer, useState } from 'react';

// third-party
import { useNavigate } from 'react-router-dom';
import { CognitoUser } from 'amazon-cognito-identity-js';
// import dayjs from 'dayjs';
import * as CognitoSDK from '@aws-sdk/client-cognito-identity-provider';
import { useMutation } from '@apollo/client';

import { Auth } from 'aws-amplify';
// reducer - state management
import { LOGIN, LOGOUT, SAVE_COGNITO_USER, SET_ASSOCIATE_SOFTWARE_TOKEN } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import { AWS_CONFIGURATION } from 'config';
import { MUTATION_LOGIN, MUTATION_LOGOUT, QUERY_ME } from 'graphql/mutations/auth';
import { AUTH_ROUTES, awsAdminClient, cleanLocalStorage, initialState, setSession } from 'utils/loginHelpers';
import { IUser } from 'views/backoffice/users/types';
import Loader from 'ui-component/Loader';
import { AWSCognitoContextType } from 'types/auth';
import { client } from 'graphql/config';
import { setForceLogoutHandler } from 'graphql/refreshTokens';

// ==============================|| AWS Cognito CONTEXT & PROVIDER ||============================== //
const AWSCognitoContext = createContext<AWSCognitoContextType | null>(null);

export const AWSCognitoProvider = ({ children }: { children: React.ReactElement }) => {
    // const location = useLocation();
    const navigate = useNavigate();
    const [state, dispatch] = useReducer(accountReducer, initialState);
    const [recoverEmail, setRecoverEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [shouldSkipSetupMFA, setShouldSkipSetupMFA] = useState(false);
    const [shouldShowSkipMFA, setShouldShowSkipMFA] = useState(true);
    const [loggedUser, setLoggedUser] = useState(null as CognitoUser | null);
    const [mfaType, setMfaType] = useState<'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | null>(null);
    const [loggedUserAuthData, setLoggedUserAuthData] = useState({ email: '', password: '' } as { email: string; password: string });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [Login, { loading: loadingLogin }] = useMutation(MUTATION_LOGIN, {
        onCompleted: (data) => {
            localStorage.setItem('tenantSelected', 'true');
            loginWithBackendData();
        }
    });

    // Backend login helpers

    const loginWithBackendData = () => {
        dispatch({
            type: LOGIN
        });
    };

    // Cognito user list helpers

    const getUsersData = async (users: IUser[]) => {
        const commands = users.map(
            (user) =>
                new CognitoSDK.AdminGetUserCommand({
                    UserPoolId: AWS_CONFIGURATION.Auth.userPoolId,
                    Username: user.email
                })
        );

        const promiseResult = await Promise.allSettled(commands.map((c) => awsAdminClient.send(c)));

        const cognitoUserData = promiseResult
            .filter((r) => r.status === 'fulfilled')
            .map((user) => {
                if (user.status === 'fulfilled') {
                    return user.value;
                }
                return null;
            });
        cognitoUserData.forEach((usr) => {
            usr?.UserAttributes?.forEach((attr) => {
                if (attr.Name === 'email' || attr.Name === 'sub') console.log(attr.Name, attr.Value);
            });
        });
        const usersMapped = users.map((user) => {
            const userFromCognito = cognitoUserData.find((cognitoData) =>
                cognitoData?.UserAttributes?.some(
                    (attr: { Name?: string; Value?: string }) => attr.Name === 'email' && attr.Value === user.email
                )
            );
            if (userFromCognito) {
                return { ...user, hasMFA: Object.keys(userFromCognito).includes('PreferredMfaSetting') };
            }
            return { ...user, hasMFA: false };
        });
        return usersMapped;
    };

    const getCurrentUser = useCallback(async () => {
        try {
            const user = await Auth.currentAuthenticatedUser();
            setLoggedUser(user);
            return user;
        } catch (error: any) {
            console.log('error getting user', error);
            return { error };
        }
    }, []);

    const validateMeResponse = (meResponse: string | null): boolean => {
        if (!meResponse) return false;

        try {
            const parsed = JSON.parse(meResponse);
            // Validate required fields
            if (!parsed.issuer || !parsed.userId || !parsed.uid || !parsed.exp) {
                return false;
            }

            // Check if token is expired
            const currentTime = Math.floor(Date.now() / 1000);
            if (currentTime >= parsed.exp) {
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error parsing me response:', error);
            return false;
        }
    };

    // Cognito auth methods

    /**
     * Promise used to login a user
     *
     * @param email {string}
     * @param password {string}
     * @returns
     */
    const login = async (
        email: string,
        password: string
    ): Promise<{ success?: boolean; error?: any; totpRequired?: boolean; totpSetUp?: true }> => {
        try {
            setLoggedUserAuthData({ email, password });
            const user = await Auth.signIn({
                username: email,
                password
            });

            setLoggedUser(user);

            if (user.preferredMFA === 'NOMFA') {
                const secretCode = await Auth.setupTOTP(user);
                localStorage.setItem('userEmail', email);
                localStorage.setItem('associateSoftwareToken', secretCode);
                dispatch({
                    type: SET_ASSOCIATE_SOFTWARE_TOKEN,
                    payload: {
                        isLoggedIn: false,
                        isCognitoLoggedIn: true,
                        associateSoftwareToken: secretCode
                    }
                });
                return { totpSetUp: true };
            }

            setMfaType('SOFTWARE_TOKEN_MFA');
            return { totpRequired: true };
        } catch (error: any) {
            console.log('login error', error);
            return { error };
        }
    };

    const setupAfterSignIn = async (user: CognitoUser | any, email?: string) => {
        const accessToken = user.signInUserSession.accessToken.jwtToken;
        const idToken = user.signInUserSession.idToken.jwtToken;
        setSession(accessToken, idToken);
        setRecoverEmail('');
        setNewPassword('');

        if (email) localStorage.setItem('userEmail', email);

        dispatch({
            type: SAVE_COGNITO_USER,
            payload: {
                isLoggedIn: false,
                user: {
                    id: user.signInUserSession.getIdToken().payload.sub,
                    email: email || user.getUsername()
                }
            }
        });

        await Login({ context: { headers: { authorization: accessToken } } });
    };

    const logout = useCallback(
        async (force = false) => {
            try {
                dispatch({ type: LOGOUT });
                setLoggedUser(null);
                setShouldSkipSetupMFA(false);
                const currentUser = await getCurrentUser();
                if (currentUser && currentUser?.preferredMFA !== 'NOMFA') {
                    await Auth.signOut();
                    cleanLocalStorage();
                }
                await client.mutate({
                    mutation: MUTATION_LOGOUT,
                    fetchPolicy: 'no-cache',
                    errorPolicy: 'all'
                });
                setShouldSkipSetupMFA(false);
                setSession(null);
            } catch (error) {
                console.log('error signing out: ', error);
            }
            if (force || AUTH_ROUTES.every((route) => !window.location.pathname.includes(route))) navigate(`/login`);
        },
        [getCurrentUser, navigate]
    );

    const recover = async (email: string = recoverEmail) => {
        await Auth.forgotPassword(email);
        setRecoverEmail(email);
    };

    const resetPassword = async (verificationCode: any, password: string) => {
        await Auth.forgotPasswordSubmit(recoverEmail, verificationCode, password);
        setNewPassword(password);
    };

    // SignUp methods

    const register = async (email: string, password: string, firstName: string, lastName: string) => {
        const res = await Auth.signUp({
            username: email,
            password,
            attributes: {
                email,
                name: firstName,
                family_name: lastName
            }
        });
        return res;
    };

    const resendConfirmationCode = async (email: string) => {
        const res = await Auth.resendSignUp(email);
        return res;
    };

    const confirmUser = async (email: string, code: string) => {
        const res = await Auth.confirmSignUp(email, code, { forceAliasCreation: true });

        return res;
    };

    // Cognito MFA helpers

    const DisableMFA = async (cognitoUserId: string | undefined) => {
        if (!cognitoUserId) return;
        const getDevicesCommand = new CognitoSDK.AdminListDevicesCommand({
            UserPoolId: AWS_CONFIGURATION.Auth.userPoolId,
            Username: cognitoUserId,
            Limit: 60
        });
        const { Devices: devices } = await awsAdminClient.send(getDevicesCommand);

        if (devices) {
            for await (const device of devices) {
                await awsAdminClient.send(
                    new CognitoSDK.AdminForgetDeviceCommand({
                        UserPoolId: AWS_CONFIGURATION.Auth.userPoolId,
                        Username: cognitoUserId,
                        DeviceKey: device.DeviceKey
                    })
                );
            }
        }

        const disableMFACommand = new CognitoSDK.AdminSetUserMFAPreferenceCommand({
            UserPoolId: AWS_CONFIGURATION.Auth.userPoolId,
            Username: cognitoUserId,
            SoftwareTokenMfaSettings: {
                Enabled: false,
                PreferredMfa: false
            }
        });
        await awsAdminClient.send(disableMFACommand);
    };

    const sendTOTP = async (code: string, rememberMe?: boolean): Promise<{ error?: any; success?: true }> => {
        try {
            const user = await Auth.confirmSignIn(loggedUser, code, mfaType);

            setShouldSkipSetupMFA(true);
            localStorage.setItem('shouldSkipSetupMFA', 'true');

            await setupAfterSignIn(user);

            return { success: true };
        } catch (error: any) {
            console.log('error sending mfa code', error);
            // the user should not be logged out post entering a wrong MFA code, so that the session can be persisted and used in the next run
            // await logout(true);
            return { error };
        }
    };

    const setPreferredMFA = async (): Promise<{ result?: any; error?: any }> => {
        try {
            const result = await Auth.setPreferredMFA(loggedUser, 'SOFTWARE_TOKEN_MFA');
            return { result };
        } catch (error: any) {
            return { error };
        }
    };

    const verifyMFASetupToken = async (code: string) => {
        const cognitoUserSession = await Auth.verifyTotpToken(loggedUser, code);
        return cognitoUserSession;
    };

    const timesMFASkipped = async () => {
        if (loggedUser) {
            const timesSkipped = Number((loggedUser as any).attributes['custom:mfa_skipped'] || 0);

            if (timesSkipped > 1000 && !loggedUserAuthData.email.includes('85623')) {
                setShouldShowSkipMFA(false);
            }
            return timesSkipped;
        }
        return 0;
    };

    const incrementSkipTimes = async () => {
        const value = (await timesMFASkipped()) + 1;

        await Auth.updateUserAttributes(loggedUser, {
            'custom:mfa_skipped': String(value)
        });
    };

    const validateUser = useCallback(async () => {
        const { data, error } = await client.query({
            query: QUERY_ME,
            fetchPolicy: 'network-only',
            errorPolicy: 'all'
        });

        if (error || !data?.me) {
            throw new Error('Failed to validate user');
        }

        const isValid = validateMeResponse(data.me);
        if (!isValid) {
            throw new Error('User validation failed');
        }
    }, []);

    useEffect(() => {
        setForceLogoutHandler(() => {
            logout(true);
        });
    }, [logout]);

    useEffect(() => {
        const init = async () => {
            if (loadingLogin) return;
            try {
                const isAuthRoute = AUTH_ROUTES.some((route) => window.location.pathname.includes(route));
                if (isAuthRoute && state.isInitialized && !state.isLoggedIn) {
                    return;
                }
                const associateSoftwareToken = window.localStorage.getItem('associateSoftwareToken');

                if (associateSoftwareToken) {
                    dispatch({
                        type: SET_ASSOCIATE_SOFTWARE_TOKEN,
                        payload: { isLoggedIn: true, associateSoftwareToken }
                    });
                }

                await validateUser();
                dispatch({ type: LOGIN });
            } catch (err) {
                console.log(err);
                const isAuthRoute = AUTH_ROUTES.some((route) => window.location.pathname.includes(route));
                if (isAuthRoute) {
                    // Already on login/auth; just sync state, don't call logout() to avoid repeated mutation and navigate
                    dispatch({ type: LOGOUT });
                    return;
                }
                // Sets redirect path to pass it when the login is performed
                const { pathname, search } = window.location;
                if (AUTH_ROUTES.every((route) => route !== pathname)) {
                    window.localStorage.setItem('redirectPath', `${pathname}${search}`);
                }
                logout();
            }
        };

        init();
    }, [logout, getCurrentUser, validateUser, loadingLogin, state.isInitialized, state.isLoggedIn]);

    // Check if the user is logged in and has a valid token when location changes
    useEffect(() => {
        (async () => {
            try {
                // Skip validation if we're on an auth route
                const isAuthRoute = AUTH_ROUTES.some((route) => location.pathname.includes(route));
                if (isAuthRoute || loadingLogin) {
                    return; // Don't validate on auth routes
                }
                await validateUser();
            } catch (error) {
                const associateSoftwareToken = window.localStorage.getItem('associateSoftwareToken');

                if (associateSoftwareToken) {
                    dispatch({
                        type: SET_ASSOCIATE_SOFTWARE_TOKEN,
                        payload: { isLoggedIn: true, associateSoftwareToken }
                    });
                }
                logout();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, logout, validateUser, loadingLogin]);

    if ((state.isInitialized !== undefined && !state.isInitialized) || loadingLogin) {
        return <Loader />;
    }

    return (
        <AWSCognitoContext.Provider
            value={{
                ...state,
                getUsersData,
                login,
                logout,
                register,
                loginWithBackendData,
                recover,
                recoverEmail,
                newPassword,
                resetPassword,
                resendConfirmationCode,
                confirmUser,
                shouldSkipSetupMFA,
                setShouldSkipSetupMFA,
                loggedUser,
                loggedUserAuthData,
                incrementSkipTimes,
                shouldShowSkipMFA,
                setShouldShowSkipMFA,
                timesMFASkipped,
                DisableMFA,
                sendTOTP,
                setPreferredMFA,
                verifyMFASetupToken
            }}
        >
            {children}
        </AWSCognitoContext.Provider>
    );
};

export default AWSCognitoContext;
